import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FaCheckCircle,
  FaMapMarkerAlt,
  FaFacebook,
  FaLinkedin,
  FaGlobe,
  FaCalendarAlt,
  FaEye,
  FaClock,
  FaExclamationCircle,
  FaPen,
  FaCheck,
  FaTimes,
  FaPhoneAlt,
} from 'react-icons/fa';
import './Profile.css';
import '../dashboard/company/CompanyVerificationPage.css';
import VerificationTab from '../dashboard/company/modules/VerificationTab';
import {
  fetchCompanyPeople,
  fetchCompanyPublicProfile,
  registerCompanyVisit,
  patchCompanyProfile,
} from '../../services/companyApi';
import {
  listCompanyJoinRequests,
  approveCompanyJoinRequest,
  rejectCompanyJoinRequest,
  unlinkCompanyMember,
} from '../../services/companyJoinRequests';
import { getCurrentAuthUser, onAuthStateChanged } from '../../firebaseAuth';
import { listPublishedPublicationsByCompany } from '../../services/publicationsApi';
import { listUnits, UNIT_TYPES, UNIT_STATUS_OPTIONS } from '../../services/unitsApi';
import { getPublicationTypeLabel } from '../../constants/publicationTypes';
import { listCompanyDocuments, COMPANY_DOCUMENT_STATUS } from '../../services/companyDocuments';
import { COMPANY_DOCUMENT_MAP } from '../../constants/companyDocuments';
import { listServiceReviews } from '../../services/reviewsApi';
import ReviewsShowcase from '../../components/ReviewsShowcase';

const FLOTTE_LOGISTICS_EXTENDED = {
  about: [],
  publications: [
    {
      title: 'Reporte de indicadores logisticos 2025',
      type: 'Reporte',
      date: 'Feb 2025',
      description:
        'Analisis de tiempos de transito, puntualidad y eficiencia de rutas en la red Flotte con recomendaciones accionables para cadenas de suministro.',
      link: '',
    },
    {
      title: 'Playbook de onboarding de operadores',
      type: 'Guia',
      date: 'Nov 2024',
      description:
        'Pasos, checklists y controles que utilizamos para certificar a cada operador antes de asignarlo a una ruta critica.',
    },
    {
      title: 'Caso de exito: retail omnicanal',
      type: 'Caso',
      date: 'Ago 2024',
      description:
        'Como habilitamos entregas next-day en 9 estados con monitoreo en vivo e integracion con ERP para un cliente de retail.',
    },
  ],
  people: [
    {
      name: 'Johannes Van',
      role: 'Director general',
      tenure: '7 anos en Flotte',
      location: 'Queretaro, Qro.',
      bio:
        'Dirige la estrategia comercial y la expansion regional, asegurando que cada corredor opere con estandares de servicio medibles.',
    },
    {
      name: 'Carolina Mendez',
      role: 'Head de operaciones',
      tenure: '5 anos en Flotte',
      location: 'CDMX',
      bio:
        'Encabeza la mesa de control y los centros de distribucion, coordinando SLA y planes de contingencia con clientes enterprise.',
    },
    {
      name: 'Oscar Ramirez',
      role: 'Lider de compliance y seguridad',
      tenure: '4 anos en Flotte',
      location: 'Monterrey, NL',
      bio:
        'Implementa los protocolos de verificacion documental, auditorias y evaluacion de riesgo para proveedores y operadores.',
    },
  ],
};

const CATEGORY_LABELS = {
  ground_transport: 'Transporte terrestre',
  security_service: 'Custodia y seguridad',
  suppliers: 'Proveedores',
  logistics_operator: 'Operador logistico',
  road_assistance: 'Asistencia vial',
  client: 'Cliente',
};

const PUBLICATION_TYPE_LABELS = {
  report: 'Reporte',
  reporte: 'Reporte',
  guide: 'Guia',
  guia: 'Guia',
  case: 'Caso',
  caso: 'Caso',
  news: 'Noticia',
  update: 'Actualizacion',
  unit: 'Oferta',
  anuncio: 'Anuncio',
};

const DEFAULT_PUBLICATION_IMAGE =
  'https://images.unsplash.com/photo-1549923746-12304f25ef6e?auto=format&fit=crop&w=640&q=80';

const COMPANY_VISIT_STORAGE_KEY = 'flotte:company:last-visit';
const COMPANY_VISIT_COOLDOWN_MS = 10 * 60 * 1000;

const UNIT_TYPE_LABELS = UNIT_TYPES.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const UNIT_STATUS_LABELS = UNIT_STATUS_OPTIONS.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const UNIT_STATUS_VARIANTS = {
  available: 'success',
  disponible: 'success',
  active: 'success',
  pending: 'warning',
  reservado: 'warning',
  transit: 'warning',
  inactive: 'danger',
  baja: 'danger',
};

const getUnitStatusVariant = status => {
  if (!status) return 'neutral';
  const normalized = String(status).trim().toLowerCase();
  return UNIT_STATUS_VARIANTS[normalized] || 'neutral';
};

const PROFILE_TAB_OPTIONS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'equipo', label: 'Equipo' },
  { id: 'verificacion', label: 'Verificación' },
];

const formatPublicationPrice = value => {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  const numericCandidate = Number(raw.replace(/[^0-9.-]/g, ''));
  if (Number.isFinite(numericCandidate) && raw.match(/\d/)) {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        maximumFractionDigits: 0,
      }).format(numericCandidate);
    } catch {
      // fallback to raw label
    }
  }

  return raw;
};

const formatPublicationDate = value => {
  if (!value) return '';
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString('es-MX', {
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const formatJoinRequestDate = value => {
  if (!value) return '—';
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) {
      return '—';
    }
    return dt.toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
};

const buildPublicationSummary = publication => {
  const candidates = [
    publication.summary,
    publication.policies,
    publication.sla,
    publication.location,
  ];
  const summary =
    candidates
      .map(item => (typeof item === 'string' ? item.trim() : ''))
      .find(Boolean) || '';

  if (summary) return summary;

  if (publication.price) {
    return `Consulta disponibilidad y precio estimado de ${publication.title || 'esta oferta'} (${publication.price}).`;
  }

  return 'Explora los detalles completos de esta publicacion para conocer disponibilidad y alcance.';
};

const isPlainObject = value =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const COMPANY_MEDIA_LABELS = {
  hero: 'El banner',
  logo: 'El logo',
};

const extractPocketBaseFieldDetail = (error, field) => {
  if (!error || !field) return null;
  const candidates = [
    error?.data,
    error?.response,
    error?.data?.data,
    error?.response?.data,
  ];

  const findInPayload = payload => {
    if (!isPlainObject(payload)) return null;
    if (payload[field]) return payload[field];
    if (isPlainObject(payload.data) && payload.data[field]) {
      return payload.data[field];
    }
    return null;
  };

  for (const candidate of candidates) {
    const detail = findInPayload(candidate);
    if (detail) {
      return detail;
    }
  }

  return null;
};

const extractByteLimit = text => {
  if (typeof text !== 'string') return null;
  const normalized = text.replace(/,/g, '');
  const bytesMatch = normalized.match(/max(?:imo)?\s*(\d+)\s*(?:bytes?|b)/i);
  if (bytesMatch) {
    return Number(bytesMatch[1]);
  }
  const mbMatch = normalized.match(/max(?:imo)?\s*(\d+(?:\.\d+)?)\s*mb/i);
  if (mbMatch) {
    return Math.round(Number(mbMatch[1]) * 1024 * 1024);
  }
  return null;
};

const formatBytes = bytes => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const kb = bytes / 1024;
  const mb = kb / 1024;
  if (mb >= 1) {
    const decimals = mb >= 10 || Math.abs(mb - Math.round(mb)) < 0.01 ? 0 : 1;
    return `${mb.toFixed(decimals)} MB`;
  }
  if (kb >= 1) {
    const decimals = kb >= 10 || Math.abs(kb - Math.round(kb)) < 0.01 ? 0 : 1;
    return `${kb.toFixed(decimals)} KB`;
  }
  return `${Math.max(1, Math.round(bytes))} B`;
};

const parseNumeric = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildMediaDetailMessage = (detail, label) => {
  if (!detail) return '';
  const normalizedDetail =
    typeof detail === 'string' ? { message: detail } : detail;
  const rawMessage =
    (typeof normalizedDetail?.message === 'string' && normalizedDetail.message.trim()) ||
    '';
  const code =
    (typeof normalizedDetail?.code === 'string' && normalizedDetail.code.trim().toLowerCase()) ||
    '';
  const sizeHint =
    extractByteLimit(rawMessage) ||
    extractByteLimit(code) ||
    parseNumeric(normalizedDetail?.max) ||
    parseNumeric(normalizedDetail?.maxSize);

  if (code.includes('size') || sizeHint) {
    const sizeLabel = sizeHint ? formatBytes(sizeHint) : '';
    if (sizeLabel) {
      return `${label} supera el límite permitido (${sizeLabel}). Usa una imagen más ligera.`;
    }
    return `${label} supera el límite permitido. Usa una imagen más ligera.`;
  }

  if (code.includes('mime') || code.includes('type')) {
    return `${label} debe ser una imagen en formato JPG, PNG o WebP.`;
  }

  if (code.includes('dimension') || rawMessage.toLowerCase().includes('dimension')) {
    return `${label} tiene dimensiones no válidas. Ajusta la imagen e inténtalo nuevamente.`;
  }

  if (rawMessage) {
    return `${label} no se pudo actualizar: ${rawMessage}`;
  }

  return `${label} no se pudo actualizar. Intenta con otra imagen.`;
};

const getCompanyMediaErrorMessage = (error, field) => {
  const detail = extractPocketBaseFieldDetail(error, field);
  if (!detail) return '';
  const label = COMPANY_MEDIA_LABELS[field] || 'El archivo';
  return buildMediaDetailMessage(detail, label);
};

const formatFollowersLabel = value => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${new Intl.NumberFormat('es-MX').format(value)} seguidores`;
  }
  const raw = String(value).trim();
  if (!raw) return '';
  const numericCandidate = Number(raw.replace(/[^0-9.-]/g, ''));
  if (Number.isFinite(numericCandidate) && raw.match(/\d/)) {
    return `${new Intl.NumberFormat('es-MX').format(numericCandidate)} seguidores`;
  }
  return raw;
};

const formatVisitCountLabel = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return '';
  }
  const rounded = Math.max(0, Math.round(numeric));
  return `${new Intl.NumberFormat('es-MX').format(rounded)} visitas`;
};

const formatMemberSinceLabel = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const ensureExternalUrl = value => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const formatLinkHost = value => {
  if (!value) return '';
  try {
    return new URL(value).hostname.replace(/^www\./i, '');
  } catch {
    return value.replace(/^https?:\/\//i, '');
  }
};

function SkeletonLine({ width = '100%', height = 16 }) {
  return (
    <span
      className="skeleton-block skeleton-line"
      style={{ width, height }}
    />
  );
}

function ProfileSkeleton() {
  return (
    <div className="profile-layout">
      <main className="profile-main">
        <header className="profile-hero card skeleton-card">
          <div className="profile-cover skeleton-block" />
        </header>

        <section className="profile-card profile-identity-card skeleton-card">
          <SkeletonLine width="50%" height={24} />
          <div className="profile-meta">
            <SkeletonLine width="40%" />
            <SkeletonLine width="32%" />
            <SkeletonLine width="28%" />
          </div>
          <div className="profile-contact skeleton-stack">
            <SkeletonLine width="60%" />
            <SkeletonLine width="45%" />
            <SkeletonLine width="30%" />
          </div>
        </section>

        <section className="profile-card profile-summary skeleton-card">
          <SkeletonLine width="45%" height={22} />
          <SkeletonLine width="100%" />
          <SkeletonLine width="95%" />
          <SkeletonLine width="88%" />
        </section>

        <section className="profile-card profile-summary skeleton-card">
          <SkeletonLine width="50%" height={22} />
          <SkeletonLine width="98%" />
          <SkeletonLine width="90%" />
          <SkeletonLine width="86%" />
        </section>
      </main>

      <aside className="profile-sidebar">
        <div className="sidebar-card profile-avatar-card skeleton-card">
          <div className="profile-avatar skeleton-block" />
        </div>
        <div className="sidebar-card profile-ad skeleton-card">
          <SkeletonLine width="36%" height={18} />
          <div className="profile-ad-figure skeleton-block" />
          <SkeletonLine width="70%" height={22} />
          <SkeletonLine width="92%" />
          <SkeletonLine width="80%" />
        </div>

        <div className="sidebar-card profile-suggestions skeleton-card">
          <SkeletonLine width="60%" height={20} />
          <ul className="profile-suggestion-list">
            {Array.from({ length: 3 }).map((_, index) => (
              <li key={index} className="profile-suggestion-item">
                <div className="profile-suggestion-avatar skeleton-block" />
                <div className="profile-suggestion-meta">
                  <SkeletonLine width="140px" />
                  <SkeletonLine width="120px" />
                </div>
                <span className="profile-suggestion-btn skeleton-block" />
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
}

export default function Profile() {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companyPeople, setCompanyPeople] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [peopleReloadKey, setPeopleReloadKey] = useState(0);
  const [companyPublications, setCompanyPublications] = useState([]);
  const [companyUnits, setCompanyUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [companyDocs, setCompanyDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [companyReviews, setCompanyReviews] = useState([]);
  const [companyReviewsLoading, setCompanyReviewsLoading] = useState(false);
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinRequestsLoading, setJoinRequestsLoading] = useState(false);
  const [joinRequestsError, setJoinRequestsError] = useState('');
  const [joinRequestsReloadKey, setJoinRequestsReloadKey] = useState(0);
  const [membershipAction, setMembershipAction] = useState({ tone: '', message: '' });
  const [processingRequestId, setProcessingRequestId] = useState('');
  const [unlinkingMemberId, setUnlinkingMemberId] = useState('');
  const [docsError, setDocsError] = useState('');
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [showFullSummary, setShowFullSummary] = useState(false);
  const [activeProfileTab, setActiveProfileTab] = useState('resumen');
  const [authModel, setAuthModel] = useState(() => getCurrentAuthUser());
  useEffect(() => onAuthStateChanged(setAuthModel), []);
  const viewerId = authModel?.id || '';
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationDraft, setLocationDraft] = useState({ city: '', state: '', country: '' });
  const [locationSaving, setLocationSaving] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [descriptionError, setDescriptionError] = useState('');
  const [bannerUploading, setBannerUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const bannerInputRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    setShowFullSummary(false);
  }, [company?.summary]);

  useEffect(() => {
    setActiveProfileTab('resumen');
  }, [slug]);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      if (!slug) {
        setCompany(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const record = await fetchCompanyPublicProfile(slug);
        if (!ignore) {
          setCompany(record);
        }
      } catch (err) {
        if (!ignore) {
          setCompany(null);
          setError(err?.message || 'No se pudo cargar el perfil.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!company?.id) {
      setCompanyPeople([]);
      setLoadingPeople(false);
      return;
    }

    let ignore = false;
    setLoadingPeople(true);

    fetchCompanyPeople(company.id)
      .then(items => {
        if (!ignore) {
          setCompanyPeople(items);
        }
      })
      .catch(() => {
        if (!ignore) {
          setCompanyPeople([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoadingPeople(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [company?.id, peopleReloadKey]);

  useEffect(() => {
    if (!company?.id) {
      setCompanyDocs([]);
      setDocsError('');
      setDocsLoaded(false);
      return;
    }
    let ignore = false;
    setDocsLoaded(false);
    setLoadingDocs(true);
    setDocsError('');
    listCompanyDocuments(company.id)
      .then(items => {
        if (ignore) return;
        setCompanyDocs(items || []);
      })
      .catch(err => {
        if (ignore) return;
        setCompanyDocs([]);
        setDocsError(err?.message || 'No se pudieron cargar los documentos.');
      })
      .finally(() => {
        if (ignore) return;
        setLoadingDocs(false);
        setDocsLoaded(true);
      });
    return () => {
      ignore = true;
    };
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id) {
      setCompanyPublications([]);
      setCompanyUnits([]);
      setLoadingUnits(false);
      return;
    }

    let ignore = false;
    setLoadingUnits(true);

    listPublishedPublicationsByCompany(company.id)
      .then(items => {
        if (!ignore) {
          setCompanyPublications(items);
        }
      })
      .catch(() => {
        if (!ignore) {
          setCompanyPublications([]);
        }
      });

    listUnits({ companyId: company.id })
      .then(items => {
        if (!ignore) {
          setCompanyUnits(items);
        }
      })
      .catch(() => {
        if (!ignore) {
          setCompanyUnits([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoadingUnits(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id) {
      setCompanyReviews([]);
      return;
    }
    let ignore = false;
    setCompanyReviewsLoading(true);
    listServiceReviews({
      targetCompanyId: company.id,
      direction: 'client_to_provider',
      limit: 6,
    })
      .then(response => {
        if (!ignore) {
          setCompanyReviews(response?.items || []);
        }
      })
      .catch(() => {
        if (!ignore) {
          setCompanyReviews([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setCompanyReviewsLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [company?.id]);

  const normalizedCompany = useMemo(() => {
    if (!company) return null;
    const industryKey = company.industry;
    const industryLabel =
      industryKey && CATEGORY_LABELS[industryKey] ? CATEGORY_LABELS[industryKey] : company.industry;
    const visitCandidates = [
      company.profileViews,
      company.views,
      company.viewCount,
      company.view_count,
      company.totalViews,
    ];
    const visitCountCandidate = visitCandidates.find(value => {
      const numeric = Number(value);
      return Number.isFinite(numeric);
    });
    const visitCount = Number.isFinite(Number(visitCountCandidate))
      ? Math.max(0, Math.round(Number(visitCountCandidate)))
      : 0;

    const slugCandidate =
      company.slug ||
      company.profileSlug ||
      company.companySlug ||
      company.handle ||
      company.permalink ||
      company.username ||
      '';
    const normalizedSlug = String(slugCandidate || slug || '').trim();
    const memberSinceCandidates = [
      company.memberSince,
      company.created,
      company.createdAt,
      company.created_at,
      company.createdOn,
      company.created_on,
      company.joinedAt,
      company.joined_at,
      company.joinedOn,
      company.joined_on,
      company.fechaAlta,
      company.fecha_alta,
    ];
    const memberSinceRaw = memberSinceCandidates.find(value => Boolean(value)) || '';
    const memberSinceLabel = formatMemberSinceLabel(memberSinceRaw);

    return {
      ...company,
      slug: normalizedSlug,
      industry: industryLabel || '',
      banner: company.banner || '',
      logo: company.logo || '',
      baseOperacion: company.baseOperacion || '',
      city: company.city || '',
      state: company.state || '',
      country: company.country || '',
      summary: company.description || company.summary || '',
      tagline: company.tagline || '',
      telefono: company.telefono || '',
      contacto: company.contacto || '',
      email: company.email || company.correo || company.contactEmail || '',
      nps: company.nps || '',
      anios: company.anios || '',
      followersLabel: formatFollowersLabel(company.followers),
      headcountLabel: company.headcount || '',
      views: visitCount,
      viewsLabel: formatVisitCountLabel(visitCount),
      docs: Array.isArray(company.docs) ? company.docs : [],
      otherPages: Array.isArray(company.otherPages) ? company.otherPages : [],
      socialLinks: {
        website: company.website || company.websiteUrl || '',
        facebook: company.facebook || company.facebookUrl || '',
        linkedin: company.linkedin || company.linkedinUrl || '',
      },
      membershipPlan: String(company.membershipPlan || 'free').trim().toLowerCase(),
      memberSinceRaw,
      memberSinceLabel,
    };
  }, [company, slug]);

  const companyRecordSlug = normalizedCompany?.slug || company?.slug || slug || '';

  useEffect(() => {
    if (!editingLocation) {
      setLocationDraft({
        city: company?.city || '',
        state: company?.state || '',
        country: company?.country || '',
      });
      setLocationError('');
    }
  }, [company?.city, company?.state, company?.country, editingLocation]);

  useEffect(() => {
    if (!editingPhone) {
      setPhoneDraft((company?.telefono || '').trim());
      setPhoneError('');
    }
  }, [company?.telefono, editingPhone]);

  useEffect(() => {
    if (!editingDescription) {
      setDescriptionDraft(normalizedCompany?.summary || '');
      setDescriptionError('');
    }
  }, [editingDescription, normalizedCompany?.summary]);

  const resolvedOwnerIds = useMemo(() => {
    if (Array.isArray(company?.ownerIds) && company.ownerIds.length) {
      return company.ownerIds;
    }
    if (Array.isArray(normalizedCompany?.ownerIds) && normalizedCompany.ownerIds.length) {
      return normalizedCompany.ownerIds;
    }
    const fallbackOwnerId =
      company?.ownerPerson?.id || normalizedCompany?.ownerPerson?.id || '';
    return fallbackOwnerId ? [fallbackOwnerId] : [];
  }, [
    company?.ownerIds,
    normalizedCompany?.ownerIds,
    company?.ownerPerson?.id,
    normalizedCompany?.ownerPerson?.id,
  ]);

  const canEditProfile = Boolean(viewerId && resolvedOwnerIds.includes(viewerId));

  useEffect(() => {
    if (!canEditProfile && activeProfileTab !== 'resumen') {
      setActiveProfileTab('resumen');
    }
  }, [canEditProfile, activeProfileTab]);

  useEffect(() => {
    if (!company?.id || !canEditProfile) {
      setJoinRequests([]);
      setJoinRequestsError('');
      setJoinRequestsLoading(false);
      return;
    }

    let ignore = false;
    setJoinRequestsLoading(true);
    setJoinRequestsError('');

    listCompanyJoinRequests(company.id)
      .then(items => {
        if (!ignore) {
          setJoinRequests(items || []);
        }
      })
      .catch(error => {
        if (!ignore) {
          setJoinRequests([]);
          setJoinRequestsError(error?.message || 'No pudimos cargar las solicitudes pendientes.');
        }
      })
      .finally(() => {
        if (!ignore) {
          setJoinRequestsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [company?.id, canEditProfile, joinRequestsReloadKey]);

  const normalizedPhoneNumber = (normalizedCompany?.telefono || '').trim();
  const phoneHref = normalizedPhoneNumber
    ? `tel:${normalizedPhoneNumber.replace(/[^0-9+]/g, '') || normalizedPhoneNumber}`
    : '';

  const refreshCompanyProfile = useCallback(async () => {
    const targetSlug = company?.slug || slug || '';
    if (!targetSlug) return;
    const refreshed = await fetchCompanyPublicProfile(targetSlug);
    setCompany(refreshed);
  }, [company?.slug, slug]);

  const triggerPeopleReload = useCallback(() => {
    setPeopleReloadKey(prev => prev + 1);
  }, []);

  const triggerJoinRequestsReload = useCallback(() => {
    setJoinRequestsReloadKey(prev => prev + 1);
  }, []);

  const startLocationEdit = () => {
    if (!canEditProfile) return;
    setLocationDraft({
      city: company?.city || '',
      state: company?.state || '',
      country: company?.country || '',
    });
    setLocationError('');
    setEditingLocation(true);
  };

  const cancelLocationEdit = () => {
    setEditingLocation(false);
    setLocationError('');
  };

  const handleLocationSave = async event => {
    event.preventDefault();
    if (!company?.id) return;
    setLocationSaving(true);
    setLocationError('');
    try {
      await patchCompanyProfile(
        company.id,
        {
          city: (locationDraft.city || '').trim(),
          state: (locationDraft.state || '').trim(),
          country: (locationDraft.country || '').trim(),
        },
        { slug: companyRecordSlug }
      );
      await refreshCompanyProfile();
      setEditingLocation(false);
    } catch (error) {
      setLocationError(error?.message || 'No se pudo actualizar la ubicacion.');
    } finally {
      setLocationSaving(false);
    }
  };

  const startPhoneEdit = () => {
    if (!canEditProfile) return;
    setPhoneDraft((normalizedCompany?.telefono || '').trim());
    setPhoneError('');
    setEditingPhone(true);
  };

  const cancelPhoneEdit = () => {
    setEditingPhone(false);
    setPhoneError('');
  };

  const handlePhoneSave = async event => {
    event.preventDefault();
    if (!company?.id) return;
    setPhoneSaving(true);
    setPhoneError('');
    try {
      await patchCompanyProfile(
        company.id,
        {
          phoneNumber: (phoneDraft || '').trim(),
        },
        { slug: companyRecordSlug }
      );
      await refreshCompanyProfile();
      setEditingPhone(false);
    } catch (error) {
      setPhoneError(error?.message || 'No se pudo actualizar el telefono.');
    } finally {
      setPhoneSaving(false);
    }
  };

  const startDescriptionEdit = () => {
    if (!canEditProfile) return;
    setDescriptionDraft(normalizedCompany?.summary || '');
    setDescriptionError('');
    setEditingDescription(true);
  };

  const cancelDescriptionEdit = () => {
    setEditingDescription(false);
    setDescriptionError('');
  };

  const handleDescriptionSave = async event => {
    event.preventDefault();
    if (!company?.id) return;
    setDescriptionSaving(true);
    setDescriptionError('');
    try {
      await patchCompanyProfile(
        company.id,
        {
          description: (descriptionDraft ?? '').trim(),
        },
        { slug: companyRecordSlug }
      );
      await refreshCompanyProfile();
      setEditingDescription(false);
    } catch (error) {
      setDescriptionError(error?.message || 'No se pudo actualizar la descripcion.');
    } finally {
      setDescriptionSaving(false);
    }
  };

  const handleApproveJoinRequest = async request => {
    if (!request?.id) return;
    setMembershipAction({ tone: '', message: '' });
    setProcessingRequestId(request.id);
    try {
      await approveCompanyJoinRequest(request, { approverId: viewerId });
      triggerJoinRequestsReload();
      triggerPeopleReload();
      setMembershipAction({
        tone: 'success',
        message: `${request.requesterName || 'El usuario'} ya puede operar con tu empresa.`,
      });
    } catch (error) {
      setMembershipAction({
        tone: 'error',
        message: error?.message || 'No pudimos aprobar la solicitud. Intenta nuevamente.',
      });
    } finally {
      setProcessingRequestId('');
    }
  };

  const handleRejectJoinRequest = async request => {
    if (!request?.id) return;
    setMembershipAction({ tone: '', message: '' });
    setProcessingRequestId(request.id);
    try {
      await rejectCompanyJoinRequest(request, { approverId: viewerId });
      triggerJoinRequestsReload();
      setMembershipAction({
        tone: 'success',
        message: `${request.requesterName || 'El usuario'} recibio la notificacion de rechazo.`,
      });
    } catch (error) {
      setMembershipAction({
        tone: 'error',
        message: error?.message || 'No pudimos rechazar la solicitud. Intenta nuevamente.',
      });
    } finally {
      setProcessingRequestId('');
    }
  };

  const handleUnlinkMember = async person => {
    if (!company?.id || !person?.id) return;
    setMembershipAction({ tone: '', message: '' });
    setUnlinkingMemberId(person.id);
    try {
      await unlinkCompanyMember(
        {
          userId: person.id,
          companyId: company.id,
          companyName: normalizedCompany?.nombre || company?.nombre || '',
        },
        { approverId: viewerId }
      );
      triggerPeopleReload();
      setMembershipAction({
        tone: 'success',
        message: `${person.name || 'El usuario'} fue desvinculado de la empresa.`,
      });
    } catch (error) {
      setMembershipAction({
        tone: 'error',
        message: error?.message || 'No pudimos desvincular al usuario. Intenta nuevamente.',
      });
    } finally {
      setUnlinkingMemberId('');
    }
  };

  const triggerBannerUpload = () => {
    if (!canEditProfile || bannerUploading) return;
    setMediaError('');
    bannerInputRef.current?.click();
  };

  const triggerLogoUpload = () => {
    if (!canEditProfile || logoUploading) return;
    setMediaError('');
    logoInputRef.current?.click();
  };

  const handleBannerFileChange = async event => {
    const file = event?.target?.files?.[0];
    if (!file || !company?.id) return;
    setMediaError('');
    setBannerUploading(true);
    try {
      await patchCompanyProfile(company.id, { heroFile: file }, { slug: companyRecordSlug });
      await refreshCompanyProfile();
    } catch (error) {
      const detailedMessage =
        getCompanyMediaErrorMessage(error, 'hero') ||
        (typeof error?.message === 'string' && error.message.trim());
      setMediaError(detailedMessage || 'No se pudo actualizar el banner.');
    } finally {
      setBannerUploading(false);
      if (event?.target) {
        event.target.value = '';
      }
    }
  };

  const handleLogoFileChange = async event => {
    const file = event?.target?.files?.[0];
    if (!file || !company?.id) return;
    setMediaError('');
    setLogoUploading(true);
    try {
      await patchCompanyProfile(company.id, { logoFile: file }, { slug: companyRecordSlug });
      await refreshCompanyProfile();
    } catch (error) {
      const detailedMessage =
        getCompanyMediaErrorMessage(error, 'logo') ||
        (typeof error?.message === 'string' && error.message.trim());
      setMediaError(detailedMessage || 'No se pudo actualizar el logo.');
    } finally {
      setLogoUploading(false);
      if (event?.target) {
        event.target.value = '';
      }
    }
  };

  useEffect(() => {
    const companyId = company?.id;
    if (!companyId) {
      return;
    }
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return;
    }

    const visitKey = COMPANY_VISIT_STORAGE_KEY;
    let cache = {};
    try {
      const stored = window.localStorage.getItem(visitKey);
      cache = stored ? JSON.parse(stored) || {} : {};
    } catch {
      cache = {};
    }

    const identifier = String(companyId);
    const now = Date.now();
    const lastVisit = Number(cache[identifier] || 0);
    const withinCooldown =
      Number.isFinite(lastVisit) && now - lastVisit < COMPANY_VISIT_COOLDOWN_MS;
    if (withinCooldown) {
      return;
    }

    cache[identifier] = now;
    try {
      window.localStorage.setItem(visitKey, JSON.stringify(cache));
    } catch {
      // ignore storage errors
    }

    const slugValue = (company?.slug || slug || '').trim();
    const viewerLabel =
      authModel?.name ||
      authModel?.username ||
      authModel?.email ||
      (viewerId ? 'Miembro de Flotte' : 'Invitado');
    const viewerUsername = authModel?.username || '';

    registerCompanyVisit({
      companyId,
      slug: slugValue,
      viewerId,
      viewerLabel,
      viewerUsername,
    })
      .then(nextCount => {
        if (typeof nextCount === 'number' && Number.isFinite(nextCount)) {
          setCompany(prev =>
            prev ? { ...prev, views: Math.max(0, Math.round(nextCount)) } : prev
          );
        }
      })
      .catch(error => {
        console.warn('No se pudo registrar la visita del perfil de empresa.', error);
      });
  }, [authModel, company?.id, company?.slug, slug, viewerId]);

  const ownerPerson = company?.ownerPerson || null;
  const normalizedSlug = (slug || '').toLowerCase();
  const companySlug = (normalizedCompany?.slug || company?.slug || '').toLowerCase();
  const matchesFlotteLogistics = ['flotte_logistics', 'flotte-logistics', 'flotte', 'flotte-sa-de-cv'].some(value => {
    return normalizedSlug === value || companySlug === value;
  });
  const docs = useMemo(() => {
    if (!companyDocs.length) return [];
    return companyDocs.map(document => {
      const definition = COMPANY_DOCUMENT_MAP[document.documentKey] || {};
      const key = document.documentKey || document.id || definition.key || '';
      const label =
        document.title ||
        document.documentTitle ||
        definition.label ||
        document.documentKey ||
        'Documento sin titulo';
      const status = (document.status || COMPANY_DOCUMENT_STATUS.NONE).toLowerCase();
      return {
        key,
        label,
        status,
        ok: status === COMPANY_DOCUMENT_STATUS.VERIFIED,
        pending: status === COMPANY_DOCUMENT_STATUS.PENDING,
        rejected: status === COMPANY_DOCUMENT_STATUS.REJECTED,
      };
    });
  }, [companyDocs]);
  const resolvedCompanyPeople = useMemo(() => {
    if (!normalizedCompany) return [];
    const baseOperation = normalizedCompany.baseOperacion || '';
    const mapped = companyPeople.map(person => ({
      ...person,
      location: person.location || baseOperation || '',
      tenure: person.tenure || '',
      bio: person.bio || '',
    }));

    if (ownerPerson) {
      const normalizedOwner = {
        ...ownerPerson,
        location: ownerPerson.location || baseOperation || '',
        tenure: ownerPerson.tenure || '',
        bio: ownerPerson.bio || '',
      };

      const existingIndex = mapped.findIndex(person => person.id && person.id === normalizedOwner.id);
      if (existingIndex >= 0) {
        const existing = mapped[existingIndex];
        mapped[existingIndex] = {
          ...existing,
          ...normalizedOwner,
          location: existing.location || normalizedOwner.location,
          tenure: existing.tenure || normalizedOwner.tenure,
          bio: existing.bio || normalizedOwner.bio,
          avatarUrl: existing.avatarUrl || normalizedOwner.avatarUrl,
        };
      } else {
        mapped.unshift(normalizedOwner);
      }
    }

    return mapped;
  }, [companyPeople, ownerPerson, normalizedCompany]);

  const resolvedPublications = useMemo(() => {
    if (companyPublications.length > 0) {
      return companyPublications.map(publication => {
        const rawType = String(publication.type || '').toLowerCase();
        const normalizedType = rawType
          ? rawType.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          : rawType;
        const fallbackLabel =
          PUBLICATION_TYPE_LABELS[normalizedType] ||
          PUBLICATION_TYPE_LABELS[rawType] ||
          '';
        const label =
          getPublicationTypeLabel(publication.type) ||
          fallbackLabel ||
          (publication.type ? publication.type : 'Publicacion');
        const dateLabel = formatPublicationDate(
          publication.availableFrom || publication.created || publication.updated
        );
        const imageUrl =
          publication.primaryImageUrl ||
          publication.productImageUrl ||
          publication.unitPhotoUrl ||
          publication.bannerUrl ||
          publication.companyHeroUrl ||
          publication.companyLogoUrl ||
          DEFAULT_PUBLICATION_IMAGE;
        const priceLabel = formatPublicationPrice(publication.price);
        return {
          id: publication.id,
          title: publication.title || 'Publicacion sin titulo',
          typeLabel: label,
          date: dateLabel,
          description: buildPublicationSummary(publication),
          imageUrl,
          priceLabel,
          location: publication.location || '',
          unitName: publication.unitName || '',
        };
      });
    }
    if (matchesFlotteLogistics) {
      return FLOTTE_LOGISTICS_EXTENDED.publications.map((publication, index) => ({
        id: `fallback-${index}`,
        title: publication.title,
        typeLabel: publication.type,
        date: publication.date || '',
        description: publication.description || '',
        imageUrl: DEFAULT_PUBLICATION_IMAGE,
        priceLabel: '',
        location: '',
        unitName: '',
      }));
    }
    return [];
  }, [companyPublications, matchesFlotteLogistics]);

  const extendedSections = useMemo(() => {
    if (!matchesFlotteLogistics) return null;
    return {
      ...FLOTTE_LOGISTICS_EXTENDED,
      people: resolvedCompanyPeople,
      publications: resolvedPublications,
    };
  }, [matchesFlotteLogistics, resolvedCompanyPeople, resolvedPublications]);

  const totalDocs = docs.length;
  const okDocs = docs.filter(doc => doc.ok).length;
  const verifiedDocNames = docs.filter(doc => doc.ok).map(doc => doc.label || doc.key).filter(Boolean);
  const pendingDocNames = docs
    .filter(doc => doc.pending)
    .map(doc => doc.label || doc.key)
    .filter(Boolean);
  const rejectedDocNames = docs
    .filter(doc => doc.rejected)
    .map(doc => doc.label || doc.key)
    .filter(Boolean);
  const pct = totalDocs > 0 ? Math.round((okDocs / totalDocs) * 100) : 0;
  const summaryParagraphs = (normalizedCompany?.summary || '').split('\n').filter(Boolean);
  const aboutCombinedText = summaryParagraphs.join(' ');
  const aboutHasMore = aboutCombinedText.length > 360;
  const peopleList = extendedSections?.people || resolvedCompanyPeople;
  const hasPeople = peopleList.length > 0;
  const hasJoinRequests = joinRequests.length > 0;
  const shouldRenderDocsCard =
    loadingDocs || docsLoaded || totalDocs > 0 || Boolean(docsError);
  const hasUnits = companyUnits.length > 0;

  if (loading) {
    return (
      <div className="prov-profile profile-shell">
        <ProfileSkeleton />
      </div>
    );
  }

  if (!normalizedCompany) {
    return (
      <div className="prov-profile profile-shell">
        {error ? <div className="profile-status error">{error}</div> : null}
        <div className="profile-empty card">
          <h2>Perfil no disponible</h2>
          <p>No encontramos la informacion publica de esta empresa.</p>
        </div>
      </div>
    );
  }

  const heroMetaItems = [];
  // location now displayed under the name
  if (normalizedCompany.followersLabel) {
    heroMetaItems.push(normalizedCompany.followersLabel);
  }
  if (normalizedCompany.headcountLabel) {
    heroMetaItems.push(normalizedCompany.headcountLabel);
  }
  if (normalizedCompany.viewsLabel) {
    heroMetaItems.push(normalizedCompany.viewsLabel);
  }

  const otherPages = normalizedCompany.otherPages;
  const membershipPlan = normalizedCompany?.membershipPlan || 'free';
  const socialLinks = normalizedCompany?.socialLinks || {};
  const hasSocialLinks = Object.values(socialLinks || {}).some(
    value => typeof value === 'string' && value.trim()
  );
  const eligibleForSocialCard = ['pro', 'premium', 'elite'].includes(membershipPlan);
  const socialItems =
    eligibleForSocialCard && hasSocialLinks
      ? [
          { key: 'website', label: 'Sitio web', href: ensureExternalUrl(socialLinks.website), icon: FaGlobe },
          { key: 'linkedin', label: 'LinkedIn', href: ensureExternalUrl(socialLinks.linkedin), icon: FaLinkedin },
          { key: 'facebook', label: 'Facebook', href: ensureExternalUrl(socialLinks.facebook), icon: FaFacebook },
        ].filter(item => item.href)
      : [];
  const shouldShowSocialCard = eligibleForSocialCard;
  const membershipBadgeLabel = (() => {
    switch (membershipPlan) {
      case 'founder':
        return 'Fundador';
      case 'premium':
        return 'Premium';
      case 'elite':
        return 'Elite';
      case 'pro':
        return 'Pro';
      default:
        return '';
    }
  })();
  const heroMemberSince = normalizedCompany.memberSinceLabel || '—';
  const hasBanner = Boolean(normalizedCompany.banner);
  const hasOtherPages = Array.isArray(otherPages) && otherPages.length > 0;
  const avatarInitial =
    normalizedCompany.nombre && normalizedCompany.nombre.length
      ? normalizedCompany.nombre.slice(0, 1).toUpperCase()
      : '?';
  const heroCoverClass = hasBanner
    ? 'profile-cover profile-header__cover'
    : 'profile-cover profile-cover--empty profile-header__cover';
  const heroMemberSinceLabel = heroMemberSince && heroMemberSince !== '—'
    ? `Miembro desde ${heroMemberSince}`
    : '';
  const isVerifiedCompany = Boolean(normalizedCompany.verificado);
  const visitsLabel = normalizedCompany.viewsLabel || '';

  const hasDocsInReview = totalDocs > 0 && !isVerifiedCompany;
  const verificationStatusVariant = isVerifiedCompany
    ? 'success'
    : hasDocsInReview
    ? 'warning'
    : 'danger';
  const verificationStatusLabel = isVerifiedCompany
    ? 'Verificada'
    : hasDocsInReview
    ? 'En revision'
    : 'Pendiente';
  const verificationStatusHelper = isVerifiedCompany
    ? 'Esta empresa cuenta con documentos avalados.'
    : hasDocsInReview
    ? 'Flotte revisa los documentos enviados por la empresa y notificara al concluir.'
    : 'La empresa aun no comparte los documentos clave para iniciar la verificacion.';

  return (
    <div className="prov-profile profile-shell">
      {error ? <div className="profile-status error">{error}</div> : null}

      <div className="profile-layout">
        <main className="profile-main">
          <section className="profile-header profile-header--company">
            <div className={heroCoverClass}>
              {hasBanner ? (
                <img
                  src={normalizedCompany.banner}
                  alt={`Banner de ${normalizedCompany.nombre || 'la empresa'}`}
                  loading="lazy"
                />
              ) : null}
              {canEditProfile ? (
                <>
                  <button
                    type="button"
                    className="profile-owner-edit profile-owner-edit--cover"
                    aria-label="Editar banner de la empresa"
                    onClick={triggerBannerUpload}
                    disabled={bannerUploading}
                  >
                    <FaPen aria-hidden="true" />
                    <span>{bannerUploading ? 'Actualizando…' : 'Editar banner'}</span>
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={bannerInputRef}
                    onChange={handleBannerFileChange}
                    style={{ display: 'none' }}
                  />
                </>
              ) : null}
            </div>
            <div className="profile-header__strip">
              <div className="profile-header__avatar profile-header__avatar--square">
                {normalizedCompany.logo ? (
                  <img src={normalizedCompany.logo} alt={normalizedCompany.nombre || 'Logo de empresa'} />
                ) : (
                  <span>{avatarInitial}</span>
                )}
                {canEditProfile ? (
                  <>
                    <button
                      type="button"
                      className="profile-owner-edit profile-owner-edit--avatar"
                      aria-label="Editar foto de perfil de la empresa"
                      onClick={triggerLogoUpload}
                      disabled={logoUploading}
                    >
                      <FaPen aria-hidden="true" />
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={logoInputRef}
                      onChange={handleLogoFileChange}
                      style={{ display: 'none' }}
                    />
                  </>
                ) : null}
              </div>
              <div className="profile-header__info">
                <div className="profile-header__headline">
                  <h1>{normalizedCompany.nombre || 'Empresa sin nombre'}</h1>
                  {isVerifiedCompany ? (
                    <span className="profile-header__badge" title="Empresa verificada" aria-label="Empresa verificada">
                      <FaCheckCircle aria-hidden="true" />
                    </span>
                  ) : null}
                  {membershipBadgeLabel ? (
                    <span className={`profile-plan-pill profile-plan-pill--${membershipPlan}`}>
                      {membershipBadgeLabel}
                    </span>
                  ) : null}
                </div>
                {(normalizedCompany.baseOperacion || heroMemberSinceLabel || canEditProfile) ? (
                  <div className="profile-header__details">
                    {editingLocation ? (
                      <form className="profile-inline-form" onSubmit={handleLocationSave}>
                        <FaMapMarkerAlt aria-hidden="true" />
                        <div className="profile-inline-grid">
                          <input
                            type="text"
                            className="profile-inline-input"
                            placeholder="Ciudad"
                            value={locationDraft.city}
                            onChange={event =>
                              setLocationDraft(prev => ({ ...prev, city: event.target.value }))
                            }
                            disabled={locationSaving}
                          />
                          <input
                            type="text"
                            className="profile-inline-input"
                            placeholder="Estado"
                            value={locationDraft.state}
                            onChange={event =>
                              setLocationDraft(prev => ({ ...prev, state: event.target.value }))
                            }
                            disabled={locationSaving}
                          />
                          <input
                            type="text"
                            className="profile-inline-input"
                            placeholder="Pais"
                            value={locationDraft.country}
                            onChange={event =>
                              setLocationDraft(prev => ({ ...prev, country: event.target.value }))
                            }
                            disabled={locationSaving}
                          />
                        </div>
                        {locationError ? (
                          <span className="profile-inline-error" role="alert">
                            {locationError}
                          </span>
                        ) : null}
                        <div className="profile-inline-actions--compact">
                          <button
                            type="button"
                            className="profile-inline-btn"
                            onClick={cancelLocationEdit}
                            disabled={locationSaving}
                          >
                            <FaTimes aria-hidden="true" />
                            <span>Cancelar</span>
                          </button>
                          <button
                            type="submit"
                            className="profile-inline-btn profile-inline-btn--primary"
                            disabled={locationSaving}
                          >
                            <FaCheck aria-hidden="true" />
                            <span>{locationSaving ? 'Guardando…' : 'Guardar'}</span>
                          </button>
                        </div>
                      </form>
                    ) : normalizedCompany.baseOperacion ? (
                      <span className="profile-header__details-item">
                        <FaMapMarkerAlt aria-hidden="true" />
                        {normalizedCompany.baseOperacion}
                        {canEditProfile ? (
                          <button
                            type="button"
                            className="profile-inline-edit"
                            aria-label="Editar ubicacion de la empresa"
                            onClick={startLocationEdit}
                          >
                            <FaPen aria-hidden="true" />
                          </button>
                        ) : null}
                      </span>
                    ) : canEditProfile ? (
                      <span className="profile-header__details-item profile-header__details-item--empty">
                        <FaMapMarkerAlt aria-hidden="true" />
                        <em>Agrega la base de operacion</em>
                        <button
                          type="button"
                          className="profile-inline-edit"
                          aria-label="Editar ubicacion de la empresa"
                          onClick={startLocationEdit}
                        >
                          <FaPen aria-hidden="true" />
                        </button>
                      </span>
                    ) : null}
                    {heroMemberSinceLabel ? (
                      <span className="profile-header__details-item">
                        <FaCalendarAlt aria-hidden="true" />
                        {heroMemberSinceLabel}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {visitsLabel || heroMetaItems.length ? (
                  <div className="profile-header__meta">
                    {visitsLabel ? (
                      <span className="profile-header__meta-stat">
                        <FaEye aria-hidden="true" />
                        {visitsLabel}
                      </span>
                    ) : null}
                    {heroMetaItems.map((item, index) => (
                      <span key={index}>{item}</span>
                    ))}
                  </div>
                ) : null}
                {(normalizedCompany.telefono || canEditProfile) ? (
                  <div className="profile-header__contact">
                    {editingPhone ? (
                      <form className="profile-inline-form" onSubmit={handlePhoneSave}>
                        <FaPhoneAlt aria-hidden="true" />
                        <input
                          id="company-phone-input"
                          type="tel"
                          className="profile-inline-input profile-inline-input--single"
                          placeholder="Ej. +52 55 1234 5678"
                          value={phoneDraft}
                          onChange={event => setPhoneDraft(event.target.value)}
                          disabled={phoneSaving}
                          aria-label="Telefono de la empresa"
                        />
                        {phoneError ? (
                          <span className="profile-inline-error" role="alert">
                            {phoneError}
                          </span>
                        ) : null}
                        <div className="profile-inline-actions--compact">
                          <button
                            type="button"
                            className="profile-inline-btn"
                            onClick={cancelPhoneEdit}
                            disabled={phoneSaving}
                          >
                            <FaTimes aria-hidden="true" />
                            <span>Cancelar</span>
                          </button>
                          <button
                            type="submit"
                            className="profile-inline-btn profile-inline-btn--primary"
                            disabled={phoneSaving}
                          >
                            <FaCheck aria-hidden="true" />
                            <span>{phoneSaving ? 'Guardando…' : 'Guardar'}</span>
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <span className="profile-header__contact-info">
                          <FaPhoneAlt aria-hidden="true" />
                          {normalizedCompany.telefono ? (
                            phoneHref ? (
                              <a href={phoneHref}>{normalizedCompany.telefono}</a>
                            ) : (
                              normalizedCompany.telefono
                            )
                          ) : (
                            <em>Agrega un telefono de contacto</em>
                          )}
                        </span>
                        {canEditProfile ? (
                          <button
                            type="button"
                            className="profile-inline-btn"
                            onClick={startPhoneEdit}
                          >
                            <FaPen aria-hidden="true" />
                            <span>Editar telefono</span>
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
                {canEditProfile && mediaError ? (
                  <p className="profile-inline-error" role="alert">
                    {mediaError}
                  </p>
                ) : null}
              </div>
            </div>
          </section>

          {canEditProfile ? (
            <nav className="profile-tabs" role="tablist" aria-label="Secciones del perfil">
              {PROFILE_TAB_OPTIONS.map(tab => (
                <button
                  key={tab.id}
                  id={`perfil-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-controls={`perfil-panel-${tab.id}`}
                  aria-selected={activeProfileTab === tab.id}
                  className={`profile-tab${activeProfileTab === tab.id ? ' is-active' : ''}`}
                  onClick={() => setActiveProfileTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          ) : null}

          {!canEditProfile || activeProfileTab === 'resumen' ? (
            <div
              className="profile-content-grid"
              role="tabpanel"
              id="perfil-panel-resumen"
              aria-labelledby="perfil-tab-resumen"
            >
            <div className="profile-column profile-column--sidebar">
              {shouldShowSocialCard ? (
                <section className="profile-card profile-social-card">
                  <span className="sidebar-tag">Canales oficiales</span>
                  <h3>Conecta con {normalizedCompany.nombre || 'la empresa'}</h3>
                  {socialItems.length ? (
                    <ul className="profile-social-list">
                      {socialItems.map(item => (
                        <li key={item.key} className="profile-social-item">
                          <span className="profile-social-icon" aria-hidden="true">
                            <item.icon />
                          </span>
                          <div className="profile-social-meta">
                            <span>{item.label}</span>
                            <a
                              href={item.href}
                              target="_blank"
                              rel="noreferrer"
                              className="profile-social-link"
                            >
                              {formatLinkHost(item.href)}
                            </a>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="profile-social-empty">
                      <p>La empresa aun no comparte enlaces publicos.</p>
                      <small>Solicita informacion directamente para conocer sus canales oficiales.</small>
                    </div>
                  )}
                </section>
              ) : null}

              {shouldRenderDocsCard ? (
                <section className="profile-card profile-verification-widget">
                  <div className="profile-verification-widget__header">
                    <div>
                      <h3>Estado de verificacion</h3>
                      <p className="muted">Seguimiento del expediente documental de la empresa.</p>
                    </div>
                    <span className={`badge badge--${verificationStatusVariant}`}>
                      {verificationStatusLabel}
                    </span>
                  </div>
                  <p className="profile-verification-widget__helper">{verificationStatusHelper}</p>
                  {docsError ? <p className="profile-docs-card__error">{docsError}</p> : null}
                  {totalDocs > 0 ? (
                    <div className="profile-docs-summary">
                      <strong>
                        {okDocs}/{totalDocs}
                      </strong>{' '}
                      documentos verificados ({pct}%)
                    </div>
                  ) : null}
                  {totalDocs > 0 ? (
                    <div className="profile-verification-widget__progress">
                      <div className="profile-verification-widget__progress-track">
                        <span style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ) : null}
                  {verifiedDocNames.length ? (
                    <div className="profile-docs-card__list">
                      <p className="profile-docs-card__highlight">Expedientes confirmados</p>
                      <ul>
                        {verifiedDocNames.map(name => (
                          <li key={name}>
                            <FaCheckCircle aria-hidden="true" />
                            <span>{name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {pendingDocNames.length ? (
                    <div className="profile-docs-card__list profile-docs-card__list--pending">
                      <p className="profile-docs-card__pending">En revision</p>
                      <ul>
                        {pendingDocNames.map(name => (
                          <li key={name}>
                            <FaClock aria-hidden="true" />
                            <span>{name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {rejectedDocNames.length ? (
                    <div className="profile-docs-card__list profile-docs-card__list--rejected">
                      <p className="profile-docs-card__rejected">Con observaciones</p>
                      <ul>
                        {rejectedDocNames.map(name => (
                          <li key={name}>
                            <FaExclamationCircle aria-hidden="true" />
                            <span>{name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div className="profile-verification-widget__actions">
                    <Link to="/dashboard/documentos" className="profile-btn outline-primary small">
                      Proceso de verificacion
                    </Link>
                  </div>
                </section>
              ) : null}

              {hasOtherPages ? (
                <section className="profile-card profile-suggestions">
                  <h3>Otras paginas vistas</h3>
                  <ul className="profile-suggestion-list">
                    {otherPages.map(page => (
                      <li key={page.id} className="profile-suggestion-item">
                        <div className="profile-suggestion-avatar">
                          {page.name ? page.name.slice(0, 1) : '?'}
                        </div>
                        <div className="profile-suggestion-meta">
                          <strong>{page.name}</strong>
                          <span>{page.meta}</span>
                        </div>
                        <button type="button" className="profile-btn ghost small">
                          + Seguir
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>

            <div className="profile-column profile-column--main">
              <section className="profile-card profile-summary">
                <div className="profile-card__header">
                  <h2>Acerca de</h2>
                  {canEditProfile && !editingDescription ? (
                    <button
                      type="button"
                      className="profile-owner-edit profile-owner-edit--inline"
                      aria-label="Editar descripcion de la empresa"
                      onClick={startDescriptionEdit}
                    >
                      <FaPen aria-hidden="true" />
                      <span>Editar descripcion</span>
                    </button>
                  ) : null}
                </div>
                {editingDescription ? (
                  <form className="profile-inline-form profile-inline-form--block" onSubmit={handleDescriptionSave}>
                    <textarea
                      className="profile-textarea"
                      rows={6}
                      value={descriptionDraft}
                      onChange={event => setDescriptionDraft(event.target.value)}
                      disabled={descriptionSaving}
                      placeholder="Describe tu empresa para los visitantes."
                    />
                    {descriptionError ? (
                      <p className="profile-inline-error" role="alert">{descriptionError}</p>
                    ) : null}
                    <div className="profile-inline-actions--compact">
                      <button
                        type="button"
                        className="profile-inline-btn"
                        onClick={cancelDescriptionEdit}
                        disabled={descriptionSaving}
                      >
                        <FaTimes aria-hidden="true" />
                        <span>Cancelar</span>
                      </button>
                      <button
                        type="submit"
                        className="profile-inline-btn profile-inline-btn--primary"
                        disabled={descriptionSaving}
                      >
                        <FaCheck aria-hidden="true" />
                        <span>{descriptionSaving ? 'Guardando…' : 'Guardar'}</span>
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div
                      className={`profile-summary__body${
                        !showFullSummary && aboutHasMore ? ' is-collapsed' : ''
                      }${aboutHasMore ? ' can-collapse' : ''}`}
                    >
                      {summaryParagraphs.length ? (
                        summaryParagraphs.map((text, index) => <p key={index}>{text}</p>)
                      ) : (
                        <p className="muted">Aun no hay un resumen publicado para esta empresa.</p>
                      )}
                    </div>
                    {aboutHasMore ? (
                      <button
                        type="button"
                        className="profile-link-btn"
                        onClick={() => setShowFullSummary(prev => !prev)}
                      >
                        {showFullSummary ? 'Mostrar menos' : 'Leer más'}
                      </button>
                    ) : null}
                  </>
                )}
                {extendedSections?.about?.length
                  ? extendedSections.about.map(section => (
                      <div key={section.title} className="profile-summary-block">
                        <h4>{section.title}</h4>
                        {section.body ? <p>{section.body}</p> : null}
                        {section.highlights ? (
                          <ul>
                            {section.highlights.map(item => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ))
                  : null}
              </section>

              <ReviewsShowcase
                containerClassName="profile-card profile-review-card"
                title="Reseñas verificadas"
                subtitle="Clientes que completaron un servicio con esta empresa"
                icon={null}
                reviews={companyReviews}
                loading={companyReviewsLoading}
                emptyMessage="Aún no hay reseñas para esta empresa."
                maxVisible={3}
              />

              <section className="profile-card profile-table-card">
                <div className="profile-card-head">
                  <div>
                    <h2>Unidades publicadas</h2>
                    <p className="muted">Inventario asociado a esta empresa.</p>
                  </div>
                </div>
                <div className="profile-table-wrapper">
                  {loadingUnits && !hasUnits ? (
                    <div className="profile-table-empty">Cargando unidades...</div>
                  ) : hasUnits ? (
                    <table className="profile-table company-data-table">
                      <thead>
                        <tr>
                          <th>Unidad</th>
                          <th>Estado</th>
                          <th>Precio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyUnits.map(unit => {
                          const unitLabel = `${UNIT_TYPE_LABELS[unit.tractorType] || unit.tractorType || '—'}${
                            unit.model ? ` · ${unit.model}` : ''
                          }`;
                          const publicationTarget =
                            unit.publicationSlug ||
                            unit.publicationId ||
                            unit.publication?.id ||
                            unit.publication?.slug ||
                            '';
                          const unitHref = publicationTarget
                            ? `/publicaciones/${publicationTarget}`
                            : '';
                          const unitLink = unitHref ? (
                            <a
                              className="profile-table-link"
                              href={unitHref}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {unitLabel}
                            </a>
                          ) : (
                            unitLabel
                          );
                          const statusLabel = UNIT_STATUS_LABELS[unit.status] || unit.status || '—';
                          const statusVariant = getUnitStatusVariant(unit.status);
                          return (
                            <tr key={unit.id}>
                              <td>{unitLink}</td>
                              <td>
                                <span className={`profile-table-badge badge--${statusVariant}`}>
                                  {statusLabel}
                                </span>
                              </td>
                              <td>
                                <span className="profile-table-price">{unit.priceLabel || '—'}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="profile-table-empty">Aun no hay unidades registradas.</div>
                  )}
                </div>
              </section>

              <section className="profile-card profile-table-card">
                <div className="profile-card-head">
                  <div>
                    <h2>Usuarios vinculados</h2>
                    <p className="muted">Colaboradores con acceso a esta empresa.</p>
                  </div>
                </div>
                <div className="profile-table-wrapper">
                  {loadingPeople && !hasPeople ? (
                    <div className="profile-table-empty">Cargando usuarios...</div>
                  ) : hasPeople ? (
                    <table className="profile-table company-data-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Rol</th>
                          <th>Ubicacion</th>
                          <th>Antiguedad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {peopleList.map(person => (
                          <tr key={person.id || person.name}>
                            <td>
                              {person.id ? (
                                <Link className="profile-table-link" to={`/usuario/${person.id}`}>
                                  {person.name || 'Sin nombre'}
                                </Link>
                              ) : (
                                person.name || 'Sin nombre'
                              )}
                            </td>
                            <td>
                              {person.role ? (
                                <span className="profile-table-badge badge--neutral">{person.role}</span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>{person.location || '—'}</td>
                            <td>{person.tenure || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="profile-table-empty">
                      Aun no hay usuarios vinculados a esta empresa.
                    </div>
                  )}
                </div>
              </section>

            </div>
          </div>
          ) : activeProfileTab === 'equipo' ? (
            <section
              className="profile-team-panel"
              role="tabpanel"
              id="perfil-panel-equipo"
              aria-labelledby="perfil-tab-equipo"
            >
              {membershipAction.message ? (
                <div
                  className={`profile-status${
                    membershipAction.tone === 'error' ? ' error' : ' success'
                  }`}
                >
                  {membershipAction.message}
                </div>
              ) : null}

              <section className="profile-card profile-table-card">
                <div className="profile-card-head">
                  <div>
                    <h2>Solicitudes de acceso</h2>
                    <p className="muted">
                      Autoriza o rechaza a los usuarios que desean unirse a tu empresa.
                    </p>
                  </div>
                  {hasJoinRequests ? (
                    <span className="profile-table-badge badge--warning">
                      {joinRequests.length} pendientes
                    </span>
                  ) : null}
                </div>
                {joinRequestsError ? (
                  <p className="profile-inline-error" role="alert">
                    {joinRequestsError}
                  </p>
                ) : null}
                <div className="profile-table-wrapper">
                  {joinRequestsLoading ? (
                    <div className="profile-table-empty">Cargando solicitudes...</div>
                  ) : hasJoinRequests ? (
                    <table className="profile-table company-data-table">
                      <thead>
                        <tr>
                          <th>Solicitante</th>
                          <th>Rol solicitado</th>
                          <th>Recibida</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {joinRequests.map(request => {
                          const createdLabel = formatJoinRequestDate(request.created);
                          const isProcessing = processingRequestId === request.id;
                          return (
                            <tr key={request.id}>
                              <td>
                                <div className="profile-team-requester">
                                  <strong>{request.requesterName || 'Usuario sin nombre'}</strong>
                                  <small>{request.requesterEmail || 'Sin correo registrado'}</small>
                                </div>
                              </td>
                              <td>{request.requesterRole || '—'}</td>
                              <td>{createdLabel}</td>
                              <td>
                                <div className="profile-team-actions">
                                  <button
                                    type="button"
                                    className="profile-btn primary small"
                                    onClick={() => handleApproveJoinRequest(request)}
                                    disabled={isProcessing}
                                  >
                                    {isProcessing ? 'Guardando…' : 'Aprobar'}
                                  </button>
                                  <button
                                    type="button"
                                    className="profile-btn outline small"
                                    onClick={() => handleRejectJoinRequest(request)}
                                    disabled={isProcessing}
                                  >
                                    {isProcessing ? 'Procesando…' : 'Rechazar'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="profile-table-empty">
                      No hay solicitudes pendientes. Comparte el enlace de registro con tu equipo.
                    </div>
                  )}
                </div>
              </section>

              <section className="profile-card profile-table-card">
                <div className="profile-card-head">
                  <div>
                    <h2>Miembros activos</h2>
                    <p className="muted">Controla quien puede operar en nombre de la empresa.</p>
                  </div>
                </div>
                <div className="profile-table-wrapper">
                  {loadingPeople && !hasPeople ? (
                    <div className="profile-table-empty">Cargando usuarios...</div>
                  ) : hasPeople ? (
                    <table className="profile-table company-data-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Rol</th>
                          <th>Ubicacion</th>
                          <th>Antiguedad</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {peopleList.map(person => {
                          const isSelf = Boolean(person.id && person.id === viewerId);
                          const isUnlinking = unlinkingMemberId === person.id;
                          return (
                            <tr key={person.id || person.name}>
                              <td>
                                {person.id ? (
                                  <Link className="profile-table-link" to={`/usuario/${person.id}`}>
                                    {person.name || 'Sin nombre'}
                                  </Link>
                                ) : (
                                  person.name || 'Sin nombre'
                                )}
                              </td>
                              <td>
                                {person.role ? (
                                  <span className="profile-table-badge badge--neutral">{person.role}</span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>{person.location || '—'}</td>
                              <td>{person.tenure || '—'}</td>
                              <td>
                                <div className="profile-team-actions">
                                  <button
                                    type="button"
                                    className="profile-btn outline small"
                                    onClick={() => handleUnlinkMember(person)}
                                    disabled={isSelf || isUnlinking}
                                    title={isSelf ? 'No puedes desvincular tu propio usuario' : ''}
                                  >
                                    {isUnlinking ? 'Desvinculando…' : 'Desvincular'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="profile-table-empty">
                      Invita a tu equipo para que puedan gestionar Flotte contigo.
                    </div>
                  )}
                </div>
              </section>
            </section>
          ) : (
            <section
              className="profile-verification-embed"
              role="tabpanel"
              id="perfil-panel-verificacion"
              aria-labelledby="perfil-tab-verificacion"
            >
              <div className="company-verification-shell company-verification-shell--embedded">
                {company?.id ? (
                  <VerificationTab
                    companyId={company.id}
                    canUpload={canEditProfile}
                    showBackLink={false}
                  />
                ) : (
                  <p className="profile-verification-embed__loading">Cargando requisitos...</p>
                )}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
